import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Layers, Star, FolderClosed, Plus, Pencil, Inbox } from "lucide-react";

// Drag-drop data key
const DRAG_TYPE = "application/x-flowgantt-project-id";

/**
 * Reusable sidebar body — used both as a fixed left column on desktop
 * and as the content of a mobile Sheet drawer.
 */
export function FoldersSidebarBody({
  folders,
  projects,
  activeKey,
  onSelect,
  onCreate,
  onEdit,
  onDropProject,
  onNavigate,
}) {
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(null);

  useEffect(() => {
    const clear = () => setDragOver(null);
    window.addEventListener("dragend", clear);
    return () => window.removeEventListener("dragend", clear);
  }, []);

  const countBy = (predicate) => projects.filter(predicate).length;
  const allCount = projects.length;
  const starredCount = countBy((p) => p.is_starred);
  const unfiledCount = countBy((p) => !p.folder_id);

  const handleDragOver = (e, key) => {
    if (e.dataTransfer.types.includes(DRAG_TYPE)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOver(key);
    }
  };
  const handleDragLeave = () => setDragOver(null);
  const handleDrop = (e, targetFolderId) => {
    e.preventDefault();
    const projectId = e.dataTransfer.getData(DRAG_TYPE);
    setDragOver(null);
    if (projectId && onDropProject) onDropProject(projectId, targetFolderId);
  };

  const Item = ({ dataKey, targetFolderId, icon, label, color, count, onEditClick }) => {
    const active = activeKey === dataKey;
    const isOver = dragOver === dataKey;
    return (
      <button
        data-testid={`folder-item-${dataKey}`}
        onClick={() => {
          onSelect(dataKey);
          if (onNavigate) onNavigate();
        }}
        onDragOver={(e) => handleDragOver(e, dataKey)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, targetFolderId)}
        title={label}
        className={`group w-full flex items-center gap-2.5 px-3 py-2 rounded-sm text-sm transition-colors text-left ${
          active
            ? "bg-neutral-950 text-white"
            : isOver
              ? "bg-[#E05236]/10 border border-dashed border-[#E05236] text-neutral-950"
              : "text-neutral-700 hover:bg-stone-100"
        }`}
      >
        {color ? (
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ background: color }}
          />
        ) : (
          <span className={`shrink-0 ${active ? "text-white" : "text-neutral-500"}`}>
            {icon}
          </span>
        )}
        <span className="flex-1 truncate">{label}</span>
        <span
          className={`text-[10px] font-semibold tabular-nums ${
            active ? "text-white/70" : "text-neutral-400"
          }`}
        >
          {count}
        </span>
        {onEditClick && (
          <span
            role="button"
            data-testid={`folder-edit-${targetFolderId}`}
            onClick={(e) => {
              e.stopPropagation();
              onEditClick();
            }}
            className={`opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded-sm cursor-pointer ${
              active ? "hover:bg-white/10" : "hover:bg-stone-200"
            }`}
          >
            <Pencil className="w-3 h-3" />
          </span>
        )}
      </button>
    );
  };

  return (
    <div data-testid="folders-sidebar-body">
      <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400 font-semibold px-3 mb-3">
        {t("folders.sidebarTitle")}
      </div>
      <nav className="space-y-0.5">
        <Item
          dataKey="all"
          targetFolderId={undefined}
          icon={<Layers className="w-4 h-4" />}
          label={t("folders.allProjects")}
          count={allCount}
        />
        <Item
          dataKey="starred"
          targetFolderId={undefined}
          icon={<Star className="w-4 h-4" />}
          label={t("folders.starred")}
          count={starredCount}
        />
        <Item
          dataKey="unfiled"
          targetFolderId={null}
          icon={<Inbox className="w-4 h-4" />}
          label={t("folders.unfiled")}
          count={unfiledCount}
        />
      </nav>

      <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400 font-semibold px-3 mt-8 mb-3 flex items-center justify-between">
        <span>{t("folders.mine")}</span>
        <button
          data-testid="folder-new-btn"
          onClick={onCreate}
          title={t("folders.new")}
          className="p-1 rounded-sm hover:bg-stone-100 text-neutral-500 hover:text-neutral-950 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
      <nav className="space-y-0.5">
        {folders.length === 0 && (
          <div className="px-3 py-2 text-xs text-neutral-400 italic">
            {t("folders.emptyHint")}
          </div>
        )}
        {folders.map((f) => (
          <Item
            key={f.folder_id}
            dataKey={f.folder_id}
            targetFolderId={f.folder_id}
            icon={<FolderClosed className="w-4 h-4" />}
            label={f.name}
            color={f.color}
            count={countBy((p) => p.folder_id === f.folder_id)}
            onEditClick={() => onEdit(f)}
          />
        ))}
      </nav>
    </div>
  );
}

/**
 * Sidebar showing All / Starred / Folders / Unfiled.
 * Desktop: fixed left column. Hidden on mobile (use <FoldersSidebarBody /> in a Sheet).
 */
export default function FoldersSidebar(props) {
  return (
    <aside
      data-testid="folders-sidebar"
      className="hidden md:block w-64 shrink-0 border-r border-stone-200 py-8 pr-4 lg:pr-6 self-start sticky top-[73px]"
    >
      <FoldersSidebarBody {...props} />
    </aside>
  );
}

export { DRAG_TYPE };
